import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { type ChartProps, CustomTooltip } from "../Utils";

interface GaugeChartProps extends Omit<ChartProps, "layout"> {
  /** Arc band width in px — stays constant however wide the card gets. */
  thickness: number;
  /** Large figure rendered under the arc. */
  centerContent?: string;
  /** Caption rendered beneath `centerContent`. */
  centerSubContent?: string;
}

/**
 * Half-donut gauge — the segments sweep 180° from left to right and the totals
 * sit in the well underneath. Use it for a distribution that reads as a single
 * score (feedback ratings, health); use `DonutChart` when the full ring is
 * wanted.
 */
export const GaugeChart = ({
  data = [],
  labelKey = "label",
  valueKey = "value",
  width = "100%",
  height = "100%",
  thickness,
  centerContent = "",
  centerSubContent = "",
  customTooltip,
  isLoading = false,
  onItemClick,
}: GaugeChartProps) => {
  const theme = useTheme();
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [maxRadius, setMaxRadius] = useState(0);
  const isEmpty = data.length === 0;
  // Recharts drops a zero-value pie entirely, so an empty gauge renders a single
  // grey placeholder arc instead.
  const chartData = isEmpty ? [{ [valueKey]: 1 }] : data;

  // Recharts can only express a fixed band width as absolute radii, so the
  // chart area is measured and the radii derived from it. The arc is pinned to
  // the bottom edge and sweeps upwards, so it is bounded by half the width and
  // by the full height — a 2:1 box is what fills exactly.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the chart area only mounts once loading finishes, so the ref has to be re-read on that flip.
  useLayoutEffect(() => {
    const node = chartAreaRef.current;
    if (!node) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width: areaWidth, height: areaHeight } = entry.contentRect;
      setMaxRadius(Math.min(areaWidth / 2, areaHeight));
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [isLoading]);

  const innerRadius = Math.max(0, maxRadius - thickness);

  if (isLoading) {
    return (
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          alignItems: "center",
          justifyContent: "flex-end",
          overflow: "hidden",
        }}
      >
        <Skeleton
          variant="circular"
          sx={{ width: "100%", aspectRatio: "1", mb: "-50%" }}
        />
      </Stack>
    );
  }

  return (
    <Box
      ref={chartAreaRef}
      sx={{
        position: "relative",
        width,
        height,
        "& g, & path": {
          outline: "none !important",
          "&:focus": { outline: "none !important" },
        },
      }}
    >
      {/* `cy="100%"` drops the pie's centre onto the bottom edge, so the 180°
          sweep fills the box upwards instead of needing a cropped, double-height
          chart area. */}
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          {!isEmpty && (
            <Tooltip
              content={
                customTooltip || (
                  <CustomTooltip labelKey={labelKey} valueKey={valueKey} />
                )
              }
              // The callout places itself — see ChartTooltip.
              allowEscapeViewBox={customTooltip ? undefined : { x: true }}
              cursor={{ fill: "transparent" }}
            />
          )}
          <Pie
            data={chartData}
            cx="50%"
            cy="100%"
            startAngle={180}
            endAngle={0}
            innerRadius={innerRadius}
            outerRadius={maxRadius}
            paddingAngle={0}
            dataKey={valueKey}
            nameKey={labelKey}
            isAnimationActive={false}
            cursor={onItemClick ? "pointer" : "default"}
            onClick={(entry) =>
              onItemClick?.(
                entry?.payload?.payload?.[valueKey],
                entry?.payload?.payload,
              )
            }
          >
            {chartData.map((entry) => (
              <Cell
                key={String(entry[labelKey] ?? "placeholder")}
                stroke="none"
                fill={entry.color || theme.palette.neutral["015"]}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      {(centerContent || centerSubContent) && (
        <Stack
          sx={{
            position: "absolute",
            bottom: -20,
            left: 0,
            right: 0,
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          <Typography variant="h5" sx={{ color: theme.palette.lightBg.high }}>
            {centerContent}
          </Typography>
          <Typography
            variant="body-xsmall"
            sx={{ color: theme.palette.lightBg.medium }}
          >
            {centerSubContent}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};
